import React,{useCallback,useEffect,useMemo,useState}from"react";
import{
  Box,Button,Dialog,DialogActions,DialogContent,
  DialogTitle,Paper,Table,TableBody,TableCell,
  TableContainer,TableHead,TableRow,TextField,Typography
}from"@mui/material";

const API_BASE="/api/purchases/nomina";

function today(){
  const date=new Date();
  return[
    date.getFullYear(),
    String(date.getMonth()+1).padStart(2,"0"),
    String(date.getDate()).padStart(2,"0")
  ].join("-");
}

function normalizeDecimal(value){
  let result=String(value??"")
    .replace(/,/g,".")
    .replace(/[^0-9.]/g,"");

  const index=result.indexOf(".");

  if(index!==-1){
    result=result.slice(0,index+1)+result.slice(index+1).replace(/\./g,"");
  }

  const parts=result.split(".");

  if(parts.length===2){
    result=`${parts[0]}.${parts[1].slice(0,2)}`;
  }

  return result;
}

function decimalValue(value){
  const number=Number(String(value??"0").replace(",","."));
  return Number.isFinite(number)?number:0;
}

function money(value){
  return decimalValue(value).toFixed(2);
}

function dateLabel(value){
  if(!value)return"-";

  const parts=String(value).split("-");

  return parts.length===3
    ?`${parts[2]}/${parts[1]}/${parts[0]}`
    :String(value);
}

function monthLabel(value){
  if(!value)return"-";

  const months=[
    "Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"
  ];

  const parts=String(value).split("-");
  const month=Number(parts[1]);

  return months[month-1]&&parts[0]
    ?`${months[month-1]} ${parts[0]}`
    :"-";
}

function employeeName(employee){
  return employee?.razon_social||employee?.nombre_comercial||"";
}

function emptyForm(){
  return{
    fecha_pago:today(),
    caja:"",
    efectivo:"",
    banco:"",
    codigo_transferencia:"",
    observacion:""
  };
}

function paymentLabel(value){
  if(value==="CAJA")return"Caja";
  if(value==="EFECTIVO")return"Efectivo";
  if(value==="BANCO")return"Transferencia";
  if(value==="MIXTO")return"Mixto";
  return"-";
}

function DecimalField({
  label,value,onChange,disabled=false,dashboardTheme
}){
  return(
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      onChange={event=>onChange(normalizeDecimal(event.target.value))}
      onBlur={()=>onChange(value===""?"":money(value))}
      inputProps={{inputMode:"decimal"}}
      sx={dashboardTheme.inputStyles}
    />
  );
}

function SummaryCard({
  label,value,dashboardTheme,highlight=false
}){
  return(
    <Paper
      elevation={0}
      sx={{
        p:1.5,
        border:`1px solid ${dashboardTheme.border_color}`,
        borderRadius:"12px",
        bgcolor:dashboardTheme.card_background,
        boxShadow:"none"
      }}
    >
      <Typography sx={{
        fontSize:12.5,
        fontWeight:400,
        color:dashboardTheme.muted_text_color,
        mb:.5
      }}>
        {label}
      </Typography>

      <Typography sx={{
        fontSize:{xs:18,sm:21},
        fontWeight:700,
        color:highlight
          ?dashboardTheme.primary_color
          :dashboardTheme.text_color
      }}>
        ${money(value)}
      </Typography>
    </Paper>
  );
}

export default function PayrollPayments({
  empresaId,
  payrollId,
  token,
  canCreate,
  canDelete,
  dashboardTheme,
  navigate,
  showError,
  showSuccess,
  showInfo,
  outlinedButtonSx,
  dangerButtonSx,
  parseError
}){
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[annulling,setAnnulling]=useState(false);
  const[nomina,setNomina]=useState(null);
  const[pagos,setPagos]=useState([]);
  const[anticipos,setAnticipos]=useState([]);
  const[aplicaciones,setAplicaciones]=useState([]);
  const[form,setForm]=useState(emptyForm);
  const[paymentToAnnul,setPaymentToAnnul]=useState(null);
  const[motivoAnulacion,setMotivoAnulacion]=useState("");

  const formSet=(key,value)=>{
    setForm(previous=>({
      ...previous,
      [key]:value
    }));
  };

  const caja=decimalValue(form.caja);
  const efectivo=decimalValue(form.efectivo);
  const banco=decimalValue(form.banco);

  const totalPago=useMemo(
    ()=>Math.round((caja+efectivo+banco)*100)/100,
    [caja,efectivo,banco]
  );

  const resumen=useMemo(()=>{
    if(!nomina)return null;

    const original=nomina.resumen||{};

    return{
      neto:original.neto??nomina.total,
      total_pagado:original.total_pagado??nomina.total_pagado,
      saldo_pendiente:original.saldo_pendiente??nomina.saldo_pendiente,
      anticipos_aplicados:
        original.anticipos_aplicados??nomina.anticipos_descontados
    };
  },[nomina]);

  const saldoPendiente=decimalValue(resumen?.saldo_pendiente);

  const montoValido=totalPago>0&&totalPago<=saldoPendiente;

  const puedeRegistrar=Boolean(
    canCreate&&
    nomina?.estado==="REGISTRADO"&&
    saldoPendiente>0
  );

  const request=useCallback(async(url,options={})=>{
    const response=await fetch(url,{
      ...options,
      credentials:"include",
      headers:{
        ...(token?{Authorization:`Bearer ${token}`}:{ }),
        ...(options.body?{"Content-Type":"application/json"}:{}),
        ...(options.headers||{})
      }
    });

    const contentType=response.headers.get("content-type")||"";

    const data=contentType.includes("application/json")
      ?await response.json()
      :{error:"El servidor devolvió una respuesta inválida."};

    if(!response.ok){
      const error=data?.error||data?.detail||data?.mensaje;

      const message=typeof error==="string"
        ?error
        :error&&typeof error==="object"
          ?Object.values(error).flat().join(" ")
          :parseError(data);

      throw new Error(message);
    }

    return data;
  },[token,parseError]);

  const loadPayments=useCallback(async()=>{
    if(!empresaId||!payrollId){
      setLoading(false);
      return;
    }

    try{
      setLoading(true);

      const params=new URLSearchParams({
        empresa_id:String(empresaId)
      });

      const[paymentData,detailData]=await Promise.all([
        request(`${API_BASE}/${payrollId}/pagos/?${params}`),
        request(`${API_BASE}/${payrollId}/?${params}`)
      ]);

      const currentPayroll=detailData.nomina||paymentData.nomina||null;

      setNomina(currentPayroll);

      setPagos(
        Array.isArray(paymentData.results)
          ?paymentData.results
          :[]
      );

      setAplicaciones(
        Array.isArray(detailData.nomina?.anticipos_aplicados)
          ?detailData.nomina.anticipos_aplicados
          :[]
      );

      if(!currentPayroll?.empleado_id){
        setAnticipos([]);
        return;
      }

      const advanceParams=new URLSearchParams({
        empresa_id:String(empresaId),
        empleado_id:String(currentPayroll.empleado_id)
      });

      const advanceData=await request(
        `${API_BASE}/anticipos/?${advanceParams}`
      );

      setAnticipos(
        Array.isArray(advanceData.results)
          ?advanceData.results
          :[]
      );
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  },[empresaId,payrollId,request,showError]);

  useEffect(()=>{
    loadPayments();
  },[loadPayments]);

  const movimientos=useMemo(()=>{
    if(!nomina)return[];

    const pagosNormales=pagos.map(pago=>({
      id:`pago-${pago.id}`,
      tipo:"PAGO",
      fecha:pago.fecha_pago,
      caja:decimalValue(pago.caja),
      efectivo:decimalValue(pago.efectivo),
      banco:decimalValue(pago.banco),
      total:decimalValue(pago.total),
      forma_pago:pago.forma_pago,
      codigo_transferencia:pago.codigo_transferencia||"",
      estado:pago.estado,
      original:pago,
      creado_en:pago.creado_en||"",
      periodo_descuento:null
    }));

    const advanceMap=new Map(
      anticipos.map(item=>[
        String(item.id),
        item
      ])
    );

    const anticiposAplicados=aplicaciones
      .map(aplicacion=>{
        const anticipo=advanceMap.get(
          String(aplicacion.anticipo_id)
        );

        if(!anticipo||anticipo.estado!=="ENTREGADO"){
          return null;
        }

        const monto=decimalValue(aplicacion.valor);

        if(monto<=0)return null;

        return{
          id:`anticipo-${aplicacion.id}`,
          tipo:"ANTICIPO",
          fecha:anticipo.fecha,
          caja:anticipo.forma_entrega==="CAJA"?monto:0,
          efectivo:anticipo.forma_entrega==="EFECTIVO"?monto:0,
          banco:anticipo.forma_entrega==="BANCO"?monto:0,
          total:monto,
          forma_pago:anticipo.forma_entrega,
          codigo_transferencia:anticipo.codigo_transferencia||"",
          estado:anticipo.estado,
          original:anticipo,
          creado_en:anticipo.creado_en||"",
          periodo_descuento:anticipo.periodo_descuento
        };
      })
      .filter(Boolean);

    return[
      ...pagosNormales,
      ...anticiposAplicados
    ].sort((a,b)=>{
      const byDate=String(b.fecha).localeCompare(String(a.fecha));

      if(byDate!==0)return byDate;

      return String(b.creado_en).localeCompare(
        String(a.creado_en)
      );
    });
  },[nomina,pagos,anticipos,aplicaciones]);

  const validatePayment=()=>{
    if(!form.fecha_pago){
      showInfo("Selecciona la fecha de pago.");
      return false;
    }

    if(totalPago<=0){
      showInfo("Ingresa un valor mayor a cero.");
      return false;
    }

    if(totalPago>saldoPendiente){
      showInfo("El pago no puede superar el saldo pendiente.");
      return false;
    }

    if(banco>0&&!form.codigo_transferencia.trim()){
      showInfo("Ingresa el código de la transferencia.");
      return false;
    }

    return true;
  };

  const savePayment=async()=>{
    if(!puedeRegistrar||saving||!validatePayment())return;

    try{
      setSaving(true);

      const payload={
        empresa_id:empresaId,
        fecha_pago:form.fecha_pago,
        caja:money(caja),
        efectivo:money(efectivo),
        banco:money(banco),
        codigo_transferencia:
          banco>0?form.codigo_transferencia.trim():"",
        observacion:form.observacion.trim()
      };

      const data=await request(
        `${API_BASE}/${payrollId}/pagos/crear/`,
        {
          method:"POST",
          body:JSON.stringify(payload)
        }
      );

      showSuccess(
        data.mensaje||"Pago registrado correctamente."
      );

      setForm(emptyForm());
      await loadPayments();
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const openAnnulDialog=pago=>{
    setPaymentToAnnul(pago);
    setMotivoAnulacion("");
  };

  const closeAnnulDialog=()=>{
    if(annulling)return;
    setPaymentToAnnul(null);
    setMotivoAnulacion("");
  };

  const annulPayment=async()=>{
    if(!paymentToAnnul||annulling)return;

    if(!motivoAnulacion.trim()){
      showInfo("Ingresa el motivo de anulación.");
      return;
    }

    try{
      setAnnulling(true);

      const data=await request(
        `${API_BASE}/pagos/${paymentToAnnul.id}/anular/`,
        {
          method:"POST",
          body:JSON.stringify({
            empresa_id:empresaId,
            motivo:motivoAnulacion.trim()
          })
        }
      );

      showSuccess(
        data.mensaje||"Pago anulado correctamente."
      );

      setPaymentToAnnul(null);
      setMotivoAnulacion("");
      await loadPayments();
    }catch(error){
      showError(error.message);
    }finally{
      setAnnulling(false);
    }
  };

  const buttonSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:36,
    px:2,
    borderRadius:"10px",
    textTransform:"none",
    fontWeight:600,
    boxShadow:"none"
  };

  const cardSx={
    ...dashboardTheme.tableStyles.wrapper,
    p:{xs:1.5,md:2},
    mb:2,
    boxShadow:"none"
  };

  const mutedSx={
    fontSize:12.5,
    color:dashboardTheme.muted_text_color
  };

  const tableStyles=dashboardTheme.tableStyles;

  const tipoColor=value=>{
    if(value==="ANTICIPO")return"#D97706";
    return dashboardTheme.primary_color;
  };

  const estadoColor=value=>{
    if(value==="ANULADO")return"#D32F2F";
    if(value==="ENTREGADO")return"#16A34A";
    return dashboardTheme.text_color;
  };

  if(loading){
    return(
      <Typography sx={{
        py:4,
        textAlign:"center",
        color:dashboardTheme.muted_text_color,
        fontSize:14
      }}>
        Cargando pagos...
      </Typography>
    );
  }

  if(!nomina){
    return(
      <Box>
        <Typography sx={{...mutedSx,mb:2}}>
          No se pudo cargar el rol de pago.
        </Typography>

        <Button
          variant="outlined"
          onClick={()=>navigate("/admin-panel/nomina/roles")}
          sx={outlinedButtonSx}
        >
          Regresar
        </Button>
      </Box>
    );
  }

  return(
    <>
      <Box sx={{
        display:"flex",
        justifyContent:"space-between",
        alignItems:{xs:"stretch",sm:"center"},
        flexDirection:{xs:"column",sm:"row"},
        gap:1.5,
        mb:2
      }}>
        <Box>
          <Typography sx={{
            fontSize:{xs:24,sm:26,md:30},
            fontWeight:600,
            color:dashboardTheme.primary_color,
            lineHeight:1.2
          }}>
            Pagos del rol
          </Typography>

          <Typography sx={{...mutedSx,mt:.5}}>
            {employeeName(nomina.empleado)}
            {" · "}
            {dateLabel(nomina.periodo_desde)}
            {" - "}
            {dateLabel(nomina.periodo_hasta)}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          onClick={()=>navigate(`/admin-panel/nomina/${payrollId}`)}
          sx={outlinedButtonSx}
        >
          Regresar
        </Button>
      </Box>

      <Box sx={{
        display:"grid",
        gridTemplateColumns:{
          xs:"1fr",
          sm:"repeat(3,minmax(0,1fr))"
        },
        gap:1.2,
        mb:2
      }}>
        <SummaryCard
          label="Neto después de anticipos"
          value={resumen.neto}
          dashboardTheme={dashboardTheme}
        />

        <SummaryCard
          label="Pagos de nómina realizados"
          value={resumen.total_pagado}
          dashboardTheme={dashboardTheme}
        />

        <SummaryCard
          label="Saldo pendiente"
          value={resumen.saldo_pendiente}
          dashboardTheme={dashboardTheme}
          highlight
        />
      </Box>

      {puedeRegistrar&&(
        <Paper elevation={0} sx={cardSx}>
          <Typography sx={{
            fontSize:17,
            fontWeight:700,
            color:dashboardTheme.text_color,
            mb:1.5
          }}>
            Registrar pago
          </Typography>

          <Box sx={{
            display:"grid",
            gridTemplateColumns:{
              xs:"1fr",
              md:"repeat(2,minmax(0,1fr))"
            },
            gap:1.5
          }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha de pago"
              value={form.fecha_pago}
              onChange={event=>formSet(
                "fecha_pago",
                event.target.value
              )}
              InputLabelProps={{shrink:true}}
              sx={dashboardTheme.inputStyles}
            />

            <Box sx={{display:{xs:"none",md:"block"}}}/>

            <DecimalField
              label="Pago desde caja"
              value={form.caja}
              onChange={value=>formSet("caja",value)}
              dashboardTheme={dashboardTheme}
            />

            <DecimalField
              label="Efectivo"
              value={form.efectivo}
              onChange={value=>formSet("efectivo",value)}
              dashboardTheme={dashboardTheme}
            />

            <DecimalField
              label="Transferencia bancaria"
              value={form.banco}
              onChange={value=>formSet("banco",value)}
              dashboardTheme={dashboardTheme}
            />

            <TextField
              fullWidth
              size="small"
              label="Código de transferencia"
              value={form.codigo_transferencia}
              disabled={banco<=0}
              onChange={event=>formSet(
                "codigo_transferencia",
                event.target.value
              )}
              sx={dashboardTheme.inputStyles}
            />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Observación (opcional)"
            value={form.observacion}
            onChange={event=>formSet(
              "observacion",
              event.target.value
            )}
            sx={{mt:1.5,...dashboardTheme.inputStyles}}
          />

          <Box sx={{
            display:"flex",
            alignItems:{xs:"stretch",sm:"center"},
            justifyContent:"space-between",
            flexDirection:{xs:"column",sm:"row"},
            flexWrap:"wrap",
            gap:1.5,
            mt:2
          }}>
            <Box>
              <Typography sx={mutedSx}>
                Total de este pago
              </Typography>

              <Typography sx={{
                fontSize:22,
                fontWeight:700,
                color:dashboardTheme.primary_color
              }}>
                ${money(totalPago)}
              </Typography>

              {totalPago>saldoPendiente&&(
                <Typography sx={{
                  fontSize:12,
                  color:"#D32F2F",
                  mt:.3
                }}>
                  El pago supera el saldo pendiente.
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={savePayment}
              disabled={
                saving||
                !montoValido||
                (banco>0&&!form.codigo_transferencia.trim())
              }
              sx={buttonSx}
            >
              {saving?"Registrando...":"Registrar pago"}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={{
        ...tableStyles.wrapper,
        boxShadow:"none"
      }}>
        <Box sx={{
          ...tableStyles.toolbar,
          display:"flex",
          justifyContent:"space-between",
          alignItems:"center",
          gap:1
        }}>
          <Typography sx={{
            fontSize:16,
            fontWeight:700,
            color:dashboardTheme.text_color
          }}>
            Historial de pagos
          </Typography>

          <Typography sx={mutedSx}>
            {movimientos.length} registro{movimientos.length===1?"":"s"}
          </Typography>
        </Box>

        <Box sx={tableStyles.tableArea}>
          <TableContainer sx={tableStyles.container}>
            <Table size="small" sx={tableStyles.table}>
              <TableHead>
                <TableRow sx={tableStyles.headRow}>
                  <TableCell sx={tableStyles.headCell}>
                    Fecha
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Tipo
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Caja
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Efectivo
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Banco
                  </TableCell>

                  <TableCell align="right" sx={tableStyles.headCell}>
                    Total
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Forma
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Referencia
                  </TableCell>

                  <TableCell sx={tableStyles.headCell}>
                    Estado
                  </TableCell>

                  {canDelete&&(
                    <TableCell
                      align="right"
                      sx={tableStyles.headCell}
                    >
                      Acciones
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {movimientos.map(item=>(
                  <TableRow
                    key={item.id}
                    hover
                    sx={tableStyles.bodyRow}
                  >
                    <TableCell sx={{
                      whiteSpace:"nowrap",
                      fontSize:12.5
                    }}>
                      {dateLabel(item.fecha)}
                    </TableCell>

                    <TableCell>
                      <Typography sx={{
                        fontSize:12.5,
                        fontWeight:600,
                        color:tipoColor(item.tipo)
                      }}>
                        {item.tipo==="ANTICIPO"
                          ?"Anticipo"
                          :"Pago de nómina"}
                      </Typography>

                      {item.tipo==="ANTICIPO"&&
                        item.periodo_descuento&&(
                        <Typography sx={{
                          fontSize:11,
                          color:dashboardTheme.muted_text_color,
                          mt:.2,
                          whiteSpace:"nowrap"
                        }}>
                          {monthLabel(item.periodo_descuento)}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      ${money(item.caja)}
                    </TableCell>

                    <TableCell align="right">
                      ${money(item.efectivo)}
                    </TableCell>

                    <TableCell align="right">
                      ${money(item.banco)}
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{
                        fontSize:13,
                        fontWeight:700,
                        color:dashboardTheme.text_color
                      }}>
                        ${money(item.total)}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{fontSize:12.5}}>
                      {paymentLabel(item.forma_pago)}
                    </TableCell>

                    <TableCell sx={{fontSize:12.5}}>
                      {item.codigo_transferencia||"-"}
                    </TableCell>

                    <TableCell>
                      <Typography sx={{
                        fontSize:12.5,
                        fontWeight:600,
                        color:estadoColor(item.estado)
                      }}>
                        {item.estado==="ANULADO"
                          ?"Anulado"
                          :item.tipo==="ANTICIPO"
                            ?"Aplicado"
                            :"Activo"}
                      </Typography>
                    </TableCell>

                    {canDelete&&(
                      <TableCell align="right">
                        {item.tipo==="PAGO"&&
                          item.estado==="ACTIVO"&&(
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={()=>openAnnulDialog(item.original)}
                            disabled={annulling||saving}
                            sx={{
                              ...dangerButtonSx,
                              minHeight:30,
                              px:1.4,
                              fontSize:12,
                              fontWeight:600
                            }}
                          >
                            Anular
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {movimientos.length===0&&(
                  <TableRow>
                    <TableCell
                      colSpan={canDelete?10:9}
                      align="center"
                      sx={tableStyles.emptyCell}
                    >
                      No hay pagos ni anticipos aplicados a este rol.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Dialog
        open={Boolean(paymentToAnnul)}
        onClose={closeAnnulDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation:0,
          sx:{
            bgcolor:dashboardTheme.card_background,
            color:dashboardTheme.text_color,
            border:`1px solid ${dashboardTheme.border_color}`,
            borderRadius:"12px",
            boxShadow:"none"
          }
        }}
      >
        <DialogTitle sx={{
          fontSize:18,
          fontWeight:700,
          color:dashboardTheme.text_color
        }}>
          Anular pago
        </DialogTitle>

        <DialogContent>
          {paymentToAnnul&&(
            <Typography sx={{
              fontSize:14,
              fontWeight:600,
              color:dashboardTheme.text_color,
              mb:2
            }}>
              Pago: ${money(paymentToAnnul.total)}
              {" · "}
              {dateLabel(paymentToAnnul.fecha_pago)}
            </Typography>
          )}

          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Motivo de anulación"
            value={motivoAnulacion}
            onChange={event=>setMotivoAnulacion(
              event.target.value
            )}
            sx={dashboardTheme.inputStyles}
          />
        </DialogContent>

        <DialogActions sx={{p:2,pt:1,gap:1}}>
          <Button
            variant="outlined"
            onClick={closeAnnulDialog}
            disabled={annulling}
            sx={outlinedButtonSx}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={annulPayment}
            disabled={annulling||!motivoAnulacion.trim()}
            sx={{
              ...buttonSx,
              bgcolor:"#D32F2F",
              "&:hover":{
                bgcolor:"#B71C1C",
                boxShadow:"none"
              }
            }}
          >
            {annulling?"Anulando...":"Confirmar anulación"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}